/**
 * ZAHL-02e (Betreiberfreigabe 14.10., E1/E2/E4, Soll 1/2) – Ops-Center, Bereich „Offene Vorgänge":
 * mandantenübergreifende Liste (nur lesend) aus GET /api/ops/offene-vorgaenge mit vier Reitern und Zähler je
 * Reiter (Zahlungsausfälle, Kündigungen, Vertragsenden, Abrechnung fehlgeschlagen), älteste zuerst, Paginierung.
 * Jede Zeile verlinkt auf das Mandanten-Detail (Abschnitte „Vertrag" und „Abrechnung").
 * Knopf „Erneut versuchen" nur in Zeilen mit Status „fehlgeschlagen" (Nachberechnung/Endabrechnung): Bestätigung im
 * Ops-Center („Erzeugen"/„Abbrechen", kein Browser-Dialog), Ergebnis inline. Kein Knopf bei „Zahlung fehlgeschlagen".
 * ZAHL-02e-2 (Betreiberfreigabe 14.10., S1/S4, Soll 2/3): abgebrochene Datensätze (in Arbeit > 15 Minuten) kommen vom
 * Backend wie fehlgeschlagen (Status „fehlgeschlagen", Fehler „abgebrochen (in Arbeit seit …)", Knopf); viertes
 * Ergebnis „in_arbeit" („Noch in Arbeit – erneuter Versuch erst nach 15 Minuten möglich.") wird inline wie die übrigen angezeigt.
 * SAMMEL-A3 (Betreiberfreigabe 22.10.): Art „Nachberechnung" wird als „Zugänge Vormonat" angezeigt; Endabrechnungs-Zeilen
 * (Vertragsende) bestätigen mit „Überspringen" und „Vertrag beendet – es wird keine Rechnung erstellt.".
 */
import { useCallback, useEffect, useState } from "react";
import { ListChecks, Loader2 } from "lucide-react";
import { opsApi, opsErrorMessage } from "@/lib/opsApi";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const TABS = [
  { id: "zahlungsausfaelle", label: "Zahlungsausfälle" },
  { id: "kuendigungen", label: "Kündigungen" },
  { id: "vertragsenden", label: "Vertragsenden" },
  { id: "abrechnung", label: "Abrechnung fehlgeschlagen" },
];
export const LEER_TEXT = "Keine offenen Vorgänge.";
export const PAGE_SIZE = 50;
const ZUSTAND_VERTRAGSENDE = { lesephase: "Lesephase", gesperrt: "gesperrt" };
const QUELLE_KUENDIGUNG = { kunde: "Kunde", clartext: "CLARTEXT" };
const dash = (v) => (v == null || v === "" ? "–" : v);
// SAMMEL-A3, Betreiberfreigabe 22.10.: Anzeige der Art (Datenfeld bleibt) und Bestätigung bei Vertragsende (Soll 3/6).
const ART_ANZEIGE = { Nachberechnung: "Zugänge Vormonat" };
export const VERTRAGSENDE_BESTAETIGUNG = "Vertrag beendet – es wird keine Rechnung erstellt.";

export function fmtBetrag(cents, currency) {
  if (cents == null || Number.isNaN(Number(cents))) return "–";
  return `${(Number(cents) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(currency || "eur").toUpperCase()}`;
}

/** Inline-Ergebnis des Knopfs („Rechnung erzeugt." / „Bereits abgerechnet – keine neue Rechnung." / „Erneut fehlgeschlagen: …" /
 *  ZAHL-02e-2: „Noch in Arbeit – erneuter Versuch erst nach 15 Minuten möglich." – neutral, weder Erfolg noch Fehler). */
export function ErgebnisText({ ergebnis, testId }) {
  const farbe = ergebnis.inArbeit ? "text-amber-700" : ergebnis.ok ? "text-emerald-700" : "text-red-700";
  return (
    <span className={`text-xs font-medium ${farbe}`} data-testid={testId} data-ergebnis={ergebnis.inArbeit ? "in_arbeit" : ergebnis.ok ? "ok" : "fehler"}>
      {ergebnis.text}
    </span>
  );
}

/** Normalisiert die Antwort des POST (oder einen HTTP-Fehler) in {ok, inArbeit, text}. */
export const zuErgebnis = (r) => ({ ok: r.ergebnis !== "fehlgeschlagen", inArbeit: r.ergebnis === "in_arbeit", text: r.text });

/** Knopf „Erneut versuchen" mit Inline-Bestätigung und Inline-Ergebnis (auch im Abschnitt „Abrechnung", Soll 3).
 *  `onDone(r)` wird immer gerufen (auch bei HTTP-Fehler, dann als ergebnis "fehlgeschlagen"), damit der Elternbereich das
 *  Ergebnis über ein Neuladen der Liste hinweg sichtbar halten kann. */
export function ErneutVersuchenKnopf({ trueupId, position, zeitraum, onDone, testId, vertragsende = false }) {
  const [phase, setPhase] = useState("idle"); // idle | confirm | busy | done
  const [ergebnis, setErgebnis] = useState(null);
  const run = async () => {
    setPhase("busy");
    let r;
    try {
      r = await opsApi.abrechnungErneutVersuchen(trueupId);
    } catch (e) {
      r = { ergebnis: "fehlgeschlagen", text: `Erneut fehlgeschlagen: ${opsErrorMessage(e)}` };
    }
    setErgebnis(zuErgebnis(r));
    setPhase("done");
    if (onDone) onDone(r);
  };
  if (phase === "done" && ergebnis) {
    return <ErgebnisText ergebnis={ergebnis} testId={`${testId}-ergebnis`} />;
  }
  if (phase === "confirm" || phase === "busy") {
    return (
      <span className="inline-flex flex-wrap items-center gap-2 text-xs text-slate-700" data-testid={`${testId}-bestaetigung`}>
        {/* SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 6): Vertragsende → „Überspringen" (Backend überspringt, keine Rechnung). */}
        <span>{vertragsende ? VERTRAGSENDE_BESTAETIGUNG : `Abrechnung für ${position} ${zeitraum} jetzt erneut erzeugen?`}</span>
        <Button size="sm" className="h-7 px-2 text-xs" disabled={phase === "busy"} onClick={run} data-testid={`${testId}-erzeugen`}>
          {phase === "busy" ? <Loader2 className="w-3 h-3 animate-spin" /> : vertragsende ? "Überspringen" : "Erzeugen"}
        </Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={phase === "busy"} onClick={() => setPhase("idle")} data-testid={`${testId}-abbrechen`}>
          Abbrechen
        </Button>
      </span>
    );
  }
  return (
    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setPhase("confirm")} data-testid={testId}>
      Erneut versuchen
    </Button>
  );
}

function MandantLink({ row, onOpenTenant, testId }) {
  return (
    <button
      type="button"
      className="text-sm font-medium text-blue-700 hover:underline text-left"
      onClick={() => onOpenTenant && onOpenTenant(row.tenant_id, row.mandant)}
      data-testid={testId}
    >
      {row.mandant || row.tenant_id}
    </button>
  );
}

function Zeilen({ tab, rows, onOpenTenant, onRetryDone, ergebnisse = {} }) {
  if (tab === "zahlungsausfaelle") {
    return rows.map((r) => (
      <TableRow key={r.id} data-testid={`ops-ov-zeile-${r.id}`}>
        <TableCell><MandantLink row={r} onOpenTenant={onOpenTenant} testId={`ops-ov-mandant-${r.id}`} /></TableCell>
        <TableCell className="text-sm" data-testid={`ops-ov-zustand-${r.id}`}>{r.zustand}</TableCell>
        <TableCell className="text-sm">{dash(r.seit_text)}</TableCell>
        <TableCell className="text-sm text-right">{fmtBetrag(r.betrag_cents, r.currency)}</TableCell>
      </TableRow>
    ));
  }
  if (tab === "kuendigungen") {
    return rows.map((r) => (
      <TableRow key={r.id} data-testid={`ops-ov-zeile-${r.id}`}>
        <TableCell><MandantLink row={r} onOpenTenant={onOpenTenant} testId={`ops-ov-mandant-${r.id}`} /></TableCell>
        <TableCell className="text-sm">{dash(r.eingang_am_text)}</TableCell>
        <TableCell className="text-sm">{dash(r.wirksam_zum_text)}</TableCell>
        <TableCell className="text-sm">{QUELLE_KUENDIGUNG[r.quelle] || dash(r.quelle)}</TableCell>
      </TableRow>
    ));
  }
  if (tab === "vertragsenden") {
    return rows.map((r) => (
      <TableRow key={r.id} data-testid={`ops-ov-zeile-${r.id}`}>
        <TableCell><MandantLink row={r} onOpenTenant={onOpenTenant} testId={`ops-ov-mandant-${r.id}`} /></TableCell>
        <TableCell className="text-sm">{dash(r.vertragsende_text)}</TableCell>
        <TableCell className="text-sm">{dash(r.lesephase_bis_text)}</TableCell>
        <TableCell className="text-sm" data-testid={`ops-ov-zustand-${r.id}`}>
          {ZUSTAND_VERTRAGSENDE[r.zustand] || dash(r.zustand)}
          {r.hinweis && <div className="text-xs text-amber-700" data-testid={`ops-ov-hinweis-${r.id}`}>{r.hinweis}</div>}
        </TableCell>
      </TableRow>
    ));
  }
  return rows.map((r) => (
    <TableRow key={r.id} data-testid={`ops-ov-zeile-${r.id}`} data-retry={r.erneut_versuchen ? "1" : "0"} data-abgebrochen={r.abgebrochen ? "1" : "0"}>
      <TableCell><MandantLink row={r} onOpenTenant={onOpenTenant} testId={`ops-ov-mandant-${r.id}`} /></TableCell>
      <TableCell className="text-sm" data-testid={`ops-ov-art-${r.id}`}>
        {ART_ANZEIGE[r.art] || r.art}{/* SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 3) */}
        {r.mailtyp && <span className="text-slate-500"> · {r.mailtyp}</span>}
      </TableCell>
      <TableCell className="text-sm">{r.art === "Mail" ? "–" : dash(r.position)}</TableCell>
      <TableCell className="text-sm">{dash(r.zeitraum_text)}</TableCell>
      <TableCell className="text-xs text-red-700" data-testid={`ops-ov-fehler-${r.id}`}>{dash(r.fehler)}</TableCell>
      <TableCell className="text-sm font-medium text-red-700" data-testid={`ops-ov-status-${r.id}`}>{r.status}</TableCell>
      <TableCell>
        {ergebnisse[r.id] ? (
          <ErgebnisText ergebnis={ergebnisse[r.id]} testId={`ops-ov-retry-${r.id}-ergebnis`} />
        ) : r.erneut_versuchen && (
          // SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 6): Art „Endabrechnung" = Vertragsende-Zeile.
          <ErneutVersuchenKnopf trueupId={r.id} position={r.position} zeitraum={r.zeitraum_text} vertragsende={r.art === "Endabrechnung"} onDone={(res) => onRetryDone(r, res)} testId={`ops-ov-retry-${r.id}`} />
        )}
      </TableCell>
    </TableRow>
  ));
}

const HEADS = {
  zahlungsausfaelle: ["Mandant", "Zustand", "Seit", "Offener Betrag"],
  kuendigungen: ["Mandant", "Eingegangen am", "Wirksam zum", "Quelle"],
  vertragsenden: ["Mandant", "Vertragsende", "Ende Lesephase", "Zustand"],
  abrechnung: ["Mandant", "Art", "Position", "Zeitraum", "Fehler", "Status", ""],
};

export default function OpsOffeneVorgaengeSection({ onOpenTenant }) {
  const [tab, setTab] = useState(TABS[0].id);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Ergebnisse des Knopfs je Datensatz-ID – bleiben über das Neuladen der Liste hinweg sichtbar (Soll 2: inline).
  const [ergebnisse, setErgebnisse] = useState({});

  const load = useCallback(() => {
    if (typeof opsApi.offeneVorgaenge !== "function") return undefined;
    let active = true;
    setLoading(true);
    setError("");
    opsApi
      .offeneVorgaenge(tab, { offset, limit: PAGE_SIZE })
      .then((d) => active && setData(d))
      .catch((e) => active && setError(opsErrorMessage(e)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [tab, offset]);

  useEffect(() => load(), [load]);

  const wechsel = (id, off = 0) => {
    setTab(id);
    setOffset(off);
    setErgebnisse({});
  };
  const onRetryDone = (row, r) => {
    setErgebnisse((prev) => ({ ...prev, [row.id]: { ...zuErgebnis(r), row } }));
    if (r.ergebnis !== "fehlgeschlagen") setTimeout(load, 0);
  };

  const rows = data?.eintraege || [];
  const total = data?.total ?? 0;
  const zaehler = data?.zaehler || {};
  // Nach dem Neuladen verschwundene Zeilen (erzeugt / bereits abgerechnet): Ergebnis bleibt oberhalb der Tabelle stehen.
  const erledigt = Object.entries(ergebnisse).filter(([id]) => !rows.some((r) => r.id === id));

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ops-offene-vorgaenge">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <ListChecks className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Offene Vorgänge</h3>
      </div>
      <div className="p-4 space-y-3">
        <div role="tablist" className="flex flex-wrap gap-2" data-testid="ops-ov-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${tab === t.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              onClick={() => wechsel(t.id)}
              data-testid={`ops-ov-tab-${t.id}`}
            >
              {t.label}
              <span className="ml-1.5 rounded-full bg-white/20 px-1.5" data-testid={`ops-ov-zaehler-${t.id}`}>{zaehler[t.id] ?? 0}</span>
            </button>
          ))}
        </div>
        {!loading && erledigt.length > 0 && (
          <ul className="space-y-1" data-testid="ops-ov-erledigt">
            {erledigt.map(([id, e]) => (
              <li key={id} className="text-xs text-slate-600" data-testid={`ops-ov-erledigt-${id}`}>
                {`${e.row.mandant || e.row.tenant_id} · ${e.row.position}${e.row.zeitraum_text ? ` ${e.row.zeitraum_text}` : ""}: `}
                <ErgebnisText ergebnis={e} testId={`ops-ov-retry-${id}-ergebnis`} />
              </li>
            ))}
          </ul>
        )}
        {loading ? (
          <div className="py-4 flex justify-center" data-testid="ops-ov-loading"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : error ? (
          <p className="text-sm text-red-600" data-testid="ops-ov-error">{error}</p>
        ) : !data ? null : rows.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="ops-ov-empty">{LEER_TEXT}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid={`ops-ov-table-${tab}`}>
              <TableHeader>
                <TableRow>{HEADS[tab].map((h, i) => <TableHead key={`${h}-${i}`} className={h === "Offener Betrag" ? "text-right" : ""}>{h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                <Zeilen tab={tab} rows={rows} onOpenTenant={onOpenTenant} onRetryDone={onRetryDone} ergebnisse={ergebnisse} />
              </TableBody>
            </Table>
          </div>
        )}
        {data && total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-slate-500" data-testid="ops-ov-pagination">
            <span>{`${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} von ${total}`}</span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={offset === 0} onClick={() => wechsel(tab, Math.max(0, offset - PAGE_SIZE))} data-testid="ops-ov-prev">Zurück</Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={offset + PAGE_SIZE >= total} onClick={() => wechsel(tab, offset + PAGE_SIZE)} data-testid="ops-ov-next">Weiter</Button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
